/**
 * A graphic's SIZE on the canvas and its RESOLUTION are two different things.
 *
 * Andrew, 2026-09-22: "We've been using PowerPoint to reduce the size to approximately 2 cm high
 * or 2 cm wide. But if this is reducing the resolution so if the user zooms in on the
 * visualisation it looks blurred, we need to change this." Until then the canvas drew every
 * graphic at its file's pixels x 0.4 (shapeBox), so the only way to control size was to shrink the
 * file. PowerPoint's Paste as PNG renders a 2 cm picture at 2x, about 116 px high, drawn 46 px
 * high. On a Retina screen that is sharp to 1.25x zoom, and the planning canvas zooms to 4x. Every
 * graphic in the library measured exactly 1.25x.
 *
 * Now Shapes.width/height hold the SIZE at the library's shared scale (still drawn x 0.4, so
 * nothing that draws graphics changed), and resolution is read from the image on the screens that
 * judge it. A file can be as large as you like and still draw 2 cm high; larger only means sharper
 * when zoomed.
 */
import { CANVAS_MAX_ZOOM, shapeBox } from '@/features/visualization/_legacy/utils/constants'

export interface PixelSize {
    width: number
    height: number
}

/** Pixels per CSS pixel on the screens this is used on. Retina, the stricter case. */
export const SCREEN_DENSITY = 2

export { CANVAS_MAX_ZOOM }

/** A vector has no pixels to lose; its recorded pixels are only a viewBox. */
export const isVectorFilename = (filename: string) => /\.svg$/i.test(filename)

/**
 * The size of a graphic, at the shared scale: 2 cm high (Andrew, 2026-09-23: "Icon should be
 * displayed as 2 cm high"). 2 cm pasted from PowerPoint at 2x is about 116 px, which is the height
 * of nearly every graphic in the library — every Business Service, OT, Storage, Identity and
 * Middleware one, and most of Compute and Endpoint — and draws 46 px on the canvas.
 *
 * THE ONLY SIZE, not a default. Both screens used to offer a height to type; Andrew, 2026-09-23:
 * "Remove the size field". A size that can be typed is a size that has to be got right, and the
 * evidence was that it was not: the two graphics anyone had reason to look at were both wrong.
 * Existing rows keep whatever they hold — the rack-unit network and compute kit is deliberately
 * not 116 — until something re-uploads them, and then they join the standard.
 */
export const STANDARD_HEIGHT = 116

/**
 * The widest a graphic may draw: 3 cm, 70 px on the canvas. Past it the graphic is scaled down as
 * a whole, keeping its shape, so it ends up LESS than 2 cm high.
 *
 * Andrew, 2026-09-24: "The VMware graphic is too big. We need a rule something like 2 cm high or
 * maximum of 4 cm wide" — a wide, flat graphic set to 2 cm high came out 190 px across, four times
 * its neighbours, and shoved every node beside it out of place in graphics mode. 4 cm was tried
 * first and was still too much: capped graphics drew 93 px, a third wider than anything else in the
 * library, and the chunky server and switch artwork looked oversized. 3 cm ("3") lines them up with
 * the widest ordinary graphics (Middleware, HMI Panel, Industrial PC).
 *
 * A very thin strip ends up a little wider than 70 px on the canvas all the same, because the canvas
 * never draws a side shorter than 20 px (SHAPE_MIN in constants.js): the hypervisor, 4:1, draws
 * 83 x 20. Keeping it legible was judged worth more than holding it to the cap exactly.
 */
export const MAX_WIDTH = 174

/**
 * The size a file draws at, at the shared scale: 2 cm high, keeping the file's shape. This is what
 * shrinking artwork in PowerPoint did by hand, except that it no longer costs any resolution —
 * the file stays as large as it came. The one place a graphic's size is decided.
 *
 * REPLACEMENTS TOO (Andrew, 2026-09-23: "I don't want to use it to have to set the size. The
 * application should set the size"). A replacement used to inherit the size of the graphic it
 * replaced, which sounds right until that size was itself an accident: under the old rule a
 * graphic's size WAS its file's pixels, so Virtual Desktop, re-uploaded sharper, stayed at the
 * 146 x 114 its 366 px file had given it. Inheriting carries those accidents forward for ever.
 */
export function defaultSize(pixels: PixelSize | null): PixelSize | null {
    if (!pixels) return null
    const width = (STANDARD_HEIGHT * pixels.width) / pixels.height
    if (width > MAX_WIDTH) {
        return { width: MAX_WIDTH, height: Math.max(1, Math.round((MAX_WIDTH * pixels.height) / pixels.width)) }
    }
    return { width: Math.max(1, Math.round(width)), height: STANDARD_HEIGHT }
}

/** The box the canvas draws a size in, in CSS pixels at 100% zoom: the canvas's own arithmetic. */
export function drawnBox(size: PixelSize): PixelSize {
    const box = shapeBox(size.width, size.height)
    return { width: box.w, height: box.h }
}

/** How far the canvas can zoom before a file of `pixels` drawn at `size` runs out of detail. */
export function sharpUpToZoom(pixels: PixelSize, size: PixelSize): number {
    const box = drawnBox(size)
    return Math.min(pixels.width / (box.width * SCREEN_DENSITY), pixels.height / (box.height * SCREEN_DENSITY))
}

/**
 * The zoom a graphic is expected to hold up to. Andrew, 2026-09-23: "Sharp up to 3 is good."
 *
 * LOWER THAN THE CANVAS'S LIMIT, on purpose. Demanding sharpness all the way to 4x called almost
 * everything blurred — including artwork that had just been re-exported larger specifically to
 * fix it — which made the warning noise rather than information. 4x is the far end of a zoom
 * nobody sits at; softening there is a fair trade for a warning that means something when it
 * appears.
 */
export const SHARP_ENOUGH_ZOOM = 3

/** The smallest file that holds up to SHARP_ENOUGH_ZOOM at `size`. What the form recommends. */
export function recommendedPixels(size: PixelSize): PixelSize {
    const box = drawnBox(size)
    return {
        width: Math.ceil(box.width * SCREEN_DENSITY * SHARP_ENOUGH_ZOOM),
        height: Math.ceil(box.height * SCREEN_DENSITY * SHARP_ENOUGH_ZOOM),
    }
}

/**
 * The three things there are to say about a file's resolution, decided in ONE place so the upload
 * form, the library cards and the edit dialog cannot disagree about when a graphic is a problem.
 *
 *   every  — holds up at any zoom the canvas offers; nothing to say beyond that
 *   enough — softens only past the far end of the zoom, which is good enough
 *   blurs  — softens within the range people actually use, and only a bigger file fixes it
 */
export type Sharpness = 'every' | 'enough' | 'blurs'

export function sharpness(zoom: number): Sharpness {
    if (zoom >= CANVAS_MAX_ZOOM) return 'every'
    if (zoom >= SHARP_ENOUGH_ZOOM) return 'enough'
    return 'blurs'
}
